/**
 * useMentionReview - React Query hooks for mention review operations
 *
 * Provides hooks for:
 * - Fetching mentions for review (query)
 * - Approving mentions (mutation)
 * - Rejecting mentions (mutation)
 * - Flagging mentions (mutation - future)
 * - Changing matched business (mutation - future)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  mentionReviewApi,
  type BulkReassignMentionsRequest,
  type BulkReassignMentionsResponse
} from '@/infrastructure/api/MentionReviewApi';
import { notificationService } from '@/infrastructure/notifications';
import type {
  MentionReviewDto,
  GetMentionsForReviewParams,
  ApproveMentionRequest,
  RejectMentionRequest,
  AlgorithmVersionsResponse
} from '@/domain/entities/MentionReview';
import type { PagedResult } from '@/domain/entities/PagedResult';

/**
 * Query keys for mention review
 */
export const mentionReviewKeys = {
  all: ['mention-review'] as const,
  lists: () => [...mentionReviewKeys.all, 'list'] as const,
  list: (params?: GetMentionsForReviewParams) => [...mentionReviewKeys.lists(), { params }] as const,
  details: () => [...mentionReviewKeys.all, 'detail'] as const,
  detail: (id: string) => [...mentionReviewKeys.details(), id] as const,
  algorithmVersions: () => [...mentionReviewKeys.all, 'algorithm-versions'] as const
};

/**
 * Sprint 038: useAlgorithmVersions - Query hook for fetching distinct algorithm versions
 *
 * Features:
 * - Longer stale time (5 minutes) - versions change infrequently
 * - Cache management
 * - Used for populating the algorithm version filter dropdown
 *
 * @returns React Query result with algorithm versions
 *
 * @example
 * const { data, isLoading } = useAlgorithmVersions();
 * // data.versions = ['1.0.0', '1.0.1']
 */
export function useAlgorithmVersions() {
  return useQuery<AlgorithmVersionsResponse, Error>({
    queryKey: mentionReviewKeys.algorithmVersions(),
    queryFn: () => mentionReviewApi.getAlgorithmVersions(),
    staleTime: 5 * 60 * 1000, // 5 minutes - versions change infrequently
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
}

/**
 * useMentionsForReview - Query hook for fetching mentions for review
 *
 * Features:
 * - Automatic refetch on window focus
 * - Cache management
 * - Error handling with toast notifications
 *
 * @param params - Query parameters (filters, pagination, sorting)
 * @param options - Query options (e.g., { enabled: boolean })
 * @returns React Query result with paged mentions data
 *
 * @example
 * const { data, isLoading, error } = useMentionsForReview({
 *   page: 1,
 *   pageSize: 25,
 *   reviewStatus: 'Pending',
 *   sortBy: 'Confidence',
 *   sortDirection: 'Asc'
 * }, { enabled: true });
 */
export function useMentionsForReview(
  params?: GetMentionsForReviewParams,
  options?: { enabled?: boolean }
) {
  return useQuery<PagedResult<MentionReviewDto>, Error>({
    queryKey: mentionReviewKeys.list(params),
    queryFn: () => mentionReviewApi.getMentionsForReview(params),
    enabled: options?.enabled ?? true,
    staleTime: 30000, // 30 seconds - data is fairly fresh
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });
}

/**
 * SESSION_076: useMentionById - Query hook for fetching a single mention by ID
 *
 * Features:
 * - Fetch single mention for detail page
 * - Cache management
 * - Error handling with toast notifications
 *
 * @param id - Mention ID (Guid)
 * @param options - Query options (e.g., { enabled: boolean })
 * @returns React Query result with single mention data
 *
 * @example
 * const { data: mention, isLoading, error } = useMentionById('123e4567-e89b-12d3-a456-426614174000', { enabled: true });
 */
export function useMentionById(
  id: string,
  options?: { enabled?: boolean }
) {
  return useQuery<MentionReviewDto, Error>({
    queryKey: mentionReviewKeys.detail(id),
    queryFn: () => mentionReviewApi.getMentionById(id),
    enabled: options?.enabled ?? true,
    staleTime: 30000, // 30 seconds - data is fairly fresh
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });
}

/**
 * useApproveMention - Mutation hook for approving a mention
 *
 * Features:
 * - Optimistic UI updates
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const approveMutation = useApproveMention();
 *
 * await approveMutation.mutateAsync({
 *   id: '123e4567-e89b-12d3-a456-426614174000',
 *   request: { adminNotes: 'Verified match' }
 * });
 */
export function useApproveMention() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string; request?: ApproveMentionRequest },
    { previousData: Map<string, PagedResult<MentionReviewDto>> }
  >({
    mutationFn: async ({ id, request }) => {
      await mentionReviewApi.approveMention(id, request);
    },
    onMutate: async ({ id }) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: mentionReviewKeys.lists() });

      // Snapshot all cached list queries for rollback
      const previousData = new Map<string, PagedResult<MentionReviewDto>>();
      const cache = queryClient.getQueriesData<PagedResult<MentionReviewDto>>({
        queryKey: mentionReviewKeys.lists()
      });
      for (const [key, data] of cache) {
        if (data) {
          previousData.set(JSON.stringify(key), data);
        }
      }

      // Optimistically remove the mention from all cached lists
      queryClient.setQueriesData<PagedResult<MentionReviewDto>>(
        { queryKey: mentionReviewKeys.lists() },
        (old) => {
          if (!old) return old;
          const filtered = old.items.filter((item) => item.id !== id);
          return {
            ...old,
            items: filtered,
            totalCount: old.totalCount - (old.items.length - filtered.length)
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      notificationService.showSuccess('Mention approved successfully');
    },
    onError: (error, _variables, context) => {
      // Restore snapshots on failure
      if (context?.previousData) {
        for (const [keyStr, data] of context.previousData) {
          const key = JSON.parse(keyStr) as readonly unknown[];
          queryClient.setQueryData(key, data);
        }
      }
      console.error('Failed to approve mention:', error);
    },
    onSettled: () => {
      // Background reconciliation — refetch to ensure consistency
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.details() });
    }
  });
}

/**
 * useRejectMention - Mutation hook for rejecting a mention
 *
 * Features:
 * - Optimistic UI updates
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const rejectMutation = useRejectMention();
 *
 * await rejectMutation.mutateAsync({
 *   id: '123e4567-e89b-12d3-a456-426614174000',
 *   request: {
 *     rejectionReason: 'WrongBusiness',
 *     adminNotes: 'Matched street name instead'
 *   }
 * });
 */
export function useRejectMention() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string; request: RejectMentionRequest },
    { previousData: Map<string, PagedResult<MentionReviewDto>> }
  >({
    mutationFn: async ({ id, request }) => {
      await mentionReviewApi.rejectMention(id, request);
    },
    onMutate: async ({ id }) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: mentionReviewKeys.lists() });

      // Snapshot all cached list queries for rollback
      const previousData = new Map<string, PagedResult<MentionReviewDto>>();
      const cache = queryClient.getQueriesData<PagedResult<MentionReviewDto>>({
        queryKey: mentionReviewKeys.lists()
      });
      for (const [key, data] of cache) {
        if (data) {
          previousData.set(JSON.stringify(key), data);
        }
      }

      // Optimistically remove the mention from all cached lists
      queryClient.setQueriesData<PagedResult<MentionReviewDto>>(
        { queryKey: mentionReviewKeys.lists() },
        (old) => {
          if (!old) return old;
          const filtered = old.items.filter((item) => item.id !== id);
          return {
            ...old,
            items: filtered,
            totalCount: old.totalCount - (old.items.length - filtered.length)
          };
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      notificationService.showSuccess('Mention rejected successfully');
    },
    onError: (error, _variables, context) => {
      // Restore snapshots on failure
      if (context?.previousData) {
        for (const [keyStr, data] of context.previousData) {
          const key = JSON.parse(keyStr) as readonly unknown[];
          queryClient.setQueryData(key, data);
        }
      }
      console.error('Failed to reject mention:', error);
    },
    onSettled: () => {
      // Background reconciliation — refetch to ensure consistency
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.details() });
    }
  });
}

/**
 * Sprint 066: useBulkReassignMentions - Mutation hook for bulk-reassigning mentions
 * from one business to another.
 *
 * Reassign-only — does NOT touch ReviewStatus, does NOT fire notifications.
 *
 * On success: shows a toast (partial-failure copy if any items failed) and invalidates
 * both the source and target business's mention list caches by invalidating the entire
 * `mentionReviewKeys.lists()` subtree. Modals that wrap this hook are responsible for
 * invalidating any non-mention caches they also depend on (e.g. `postReviewKeys`).
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const bulkReassign = useBulkReassignMentions();
 *
 * const response = await bulkReassign.mutateAsync({
 *   newBusinessId: 'abc...',
 *   sourceBusinessId: 'def...',
 *   mentionIds: ['m1', 'm2', 'm3'],
 *   adminNotes: 'Mario\'s → Big Mario\'s cleanup'
 * });
 * if (response.failedCount > 0) { ... render errors ... }
 */
export function useBulkReassignMentions() {
  const queryClient = useQueryClient();

  return useMutation<BulkReassignMentionsResponse, Error, BulkReassignMentionsRequest>({
    mutationFn: async (request) => {
      return mentionReviewApi.bulkReassignMentions(request);
    },
    onSuccess: (data) => {
      if (data.failedCount === 0) {
        const plural = data.reassignedCount === 1 ? '' : 's';
        notificationService.showSuccess(
          `Reassigned ${data.reassignedCount} mention${plural}`
        );
      } else {
        const total = data.reassignedCount + data.failedCount;
        notificationService.showError(
          `Reassigned ${data.reassignedCount} of ${total}; ${data.failedCount} failed`
        );
      }
    },
    onError: (error: Error) => {
      console.error('Bulk reassignment failed:', error);
      notificationService.showError(error.message || 'Bulk reassignment failed');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.details() });
    }
  });
}

/**
 * useFlagMention - Mutation hook for flagging a mention
 * (Future enhancement - endpoint not yet implemented)
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useFlagMention() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string; notes?: string }
  >({
    mutationFn: async ({ id, notes }) => {
      await mentionReviewApi.flagMention(id, notes);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: mentionReviewKeys.lists()
      });

      notificationService.showSuccess('Mention flagged successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to flag mention:', error);
    }
  });
}

/**
 * useChangeMatchedBusiness - Mutation hook for changing matched business
 * (Future enhancement - endpoint not yet implemented)
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useChangeMatchedBusiness() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string; newBusinessId: string; notes?: string }
  >({
    mutationFn: async ({ id, newBusinessId, notes }) => {
      await mentionReviewApi.changeMatchedBusiness(id, newBusinessId, notes);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: mentionReviewKeys.lists()
      });

      notificationService.showSuccess('Matched business updated successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to change matched business:', error);
    }
  });
}
